import React from 'react';
import { CSVArtist, ValidationError } from './types';
import Swal from 'sweetalert2';

interface CSVPreviewProps {
  data: CSVArtist[];
  errors: ValidationError[];
  onImport: () => void;
  onBack: () => void;
  uploading: boolean;
}

const CSVPreview: React.FC<CSVPreviewProps> = ({ data, errors, onImport, onBack, uploading }) => {
  // Add defensive programming to handle undefined errors AND data
  const safeErrors = errors || [];
  const safeData = data || [];
  const validRows = safeData.filter((_, index) => 
    !safeErrors.some(error => error.row === index + 2 && error.severity === 'error')
  );

  const warningRows = safeData.filter((_, index) => 
    safeErrors.some(error => error.row === index + 2 && error.severity === 'warning') &&
    !safeErrors.some(error => error.row === index + 2 && error.severity === 'error')
  );

  const errorRows = safeData.filter((_, index) => 
    safeErrors.some(error => error.row === index + 2 && error.severity === 'error')
  );

  const getRowStatus = (index: number) => {
    const rowErrors = safeErrors.filter(error => error.row === index + 2);
    if (rowErrors.some(error => error.severity === 'error')) return 'error';
    if (rowErrors.some(error => error.severity === 'warning')) return 'warning';
    return 'valid';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'valid':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const handleImport = () => {
    onImport();
    Swal.fire({
      title: 'Import Complete!',
      text: `Successfully imported ${validRows.length} artists. ${errorRows.length > 0 ? errorRows.length + ' rows were skipped due to errors.' : ''}`,
      icon: 'success',
      confirmButtonText: 'OK'
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-green-50">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-900">Valid Artists</p>
              <p className="text-2xl font-bold text-green-900">{validRows.length}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-yellow-50">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-yellow-900">Warnings</p>
              <p className="text-2xl font-bold text-yellow-900">{warningRows.length}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-red-50">
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <div>
              <p className="text-sm font-medium text-red-900">Errors</p>
              <p className="text-2xl font-bold text-red-900">{errorRows.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error Summary */}
      {safeErrors.length > 0 && (
        <div className="space-y-4">
          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <h4 className="mb-2 font-medium text-red-900">❌ Errors (Must Fix Before Import):</h4>
            <div className="space-y-1 overflow-y-auto max-h-40">
              {safeErrors.map((error, index) => (
                <div key={index} className="text-sm text-red-700">
                  <span className="font-medium">Row {error.row}:</span> {error.message}
                  <span className="text-red-500"> ({error.field})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {validRows.length === 0 && safeErrors.length > 0 && (
        <div className="p-4 mb-4 text-red-800 bg-red-100 border border-red-200 rounded">
          All rows have errors. Please fix the issues above and re-upload your CSV.
        </div>
      )}

      {/* Preview Table */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="overflow-y-auto max-h-96">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Artist Name
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Spotify URI
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Apple ID
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  ASIN
                </th>
                <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Issues
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {safeData.map((row, index) => {
              
                const status = getRowStatus(index);
                const rowErrors = safeErrors.filter(error => error.row === index + 2);
                return (
                  <tr key={index} className={`
                    ${status === 'error' ? 'bg-red-50' : ''}
                    ${status === 'warning' ? 'bg-yellow-50' : ''}
                    ${status === 'valid' ? 'bg-green-50' : ''}
                  `}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(status)}
                        <span className="ml-2 text-xs font-medium">
                          {status === 'valid' ? 'Valid' : 
                           status === 'warning' ? 'Warning' : 'Error'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {row['artistName']}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {row['spotifyURI']}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {row['appleID']}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                      {row['asin']}
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      {rowErrors.length > 0 && (
                        <div className="space-y-1">
                          {rowErrors.map((error, i) => (
                            <div key={i} className={`text-xs ${
                              error.severity === 'error' ? 'text-red-600' : 'text-yellow-600'
                            }`}>
                              {error.field}: {error.message}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={uploading}
          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          Back to Upload
        </button>
        
        <div className="flex space-x-3">
          {errorRows.length > 0 && (
            <div className="flex items-center px-3 py-2 text-sm text-red-700 bg-red-100 rounded-md">
              Fix {errorRows.length} errors first
            </div>
          )}
          
          {validRows.length > 0 && (
            <button
              onClick={handleImport}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {uploading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  Importing...
                </div>
              ) : (
                `Import ${validRows.length} Valid Artists`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CSVPreview;